import { tryCatch } from '@aline/common';
import * as Sentry from '@sentry/react';
import type { z } from 'zod';

/**
 * Parses a code-block body as JSON and validates it against a zod schema.
 * Captures malformed payloads to Sentry by default so AI/agent output
 * regressions are not silently swallowed by the UI.
 */
export function parseCodeBlockJson<TSchema extends z.ZodTypeAny>(
  rawCode: string,
  schema: TSchema,
  source: string,
  options: { captureErrors?: boolean } = {}
): { data: z.infer<TSchema>; ok: true } | { ok: false; reason: 'invalid_json' | 'invalid_schema' } {
  const shouldCaptureErrors = options.captureErrors ?? true;
  const parsedJson = tryCatch(() => JSON.parse(rawCode));
  if (!parsedJson.ok) {
    if (shouldCaptureErrors) {
      Sentry.captureException(new Error(`${source}: code block is not valid JSON`), {
        extra: { rawCodeLength: rawCode.length },
      });
    }
    return { ok: false, reason: 'invalid_json' };
  }

  const parsedSchema = schema.safeParse(parsedJson.result);
  if (!parsedSchema.success) {
    if (shouldCaptureErrors) {
      Sentry.captureException(new Error(`${source}: payload failed schema validation`), {
        extra: {
          issues: parsedSchema.error.issues.map(issue => ({
            code: issue.code,
            message: issue.message,
            path: issue.path,
          })),
          rawCodeLength: rawCode.length,
        },
      });
    }
    return { ok: false, reason: 'invalid_schema' };
  }

  return { data: parsedSchema.data, ok: true };
}
